import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { 
  Sparkles, 
  TrendingUp, 
  ShoppingBag, 
  Award, 
  GraduationCap, 
  Smile, 
  Compass,
  Video,
  Image as ImageIcon,
  Music,
  Package,
  ArrowRight,
  ArrowLeft,
  X,
  Check,
  Zap,
  Star,
  ChevronRight,
  Clock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const iconMap = {
  TrendingUp, ShoppingBag, Award, GraduationCap, Smile, Compass,
  Video, Image: ImageIcon, Music, Package
};

// Step indicator component
const StepIndicator = ({ currentStep, totalSteps }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {Array.from({ length: totalSteps }).map((_, idx) => (
      <div
        key={idx}
        className={`h-2 rounded-full transition-all duration-300 ${
          idx === currentStep 
            ? 'w-8 bg-primary' 
            : idx < currentStep 
              ? 'w-2 bg-primary/50' 
              : 'w-2 bg-muted'
        }`}
      />
    ))}
  </div>
);

const OnboardingFlow = ({ onComplete }) => {
  const { token, refreshUser } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(0);
  const [goals, setGoals] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [selectedContentType, setSelectedContentType] = useState(null);
  const [recommendedTemplates, setRecommendedTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Fetch onboarding options on mount
  useEffect(() => {
    fetchOptions();
  }, []);

  // Fetch template recommendations when goal and content type are selected
  useEffect(() => {
    if (selectedGoal && selectedContentType) {
      fetchRecommendations();
    }
  }, [selectedGoal, selectedContentType]);

  const fetchOptions = async () => {
    try {
      const response = await axios.get(`${API}/api/onboarding/options`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoals(response.data.goals || []);
      setContentTypes(response.data.content_types || []);
    } catch (error) {
      console.error('Failed to fetch onboarding options:', error);
      toast.error('Failed to load onboarding options');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    try {
      const response = await axios.get(
        `${API}/api/onboarding/recommendations?goal=${selectedGoal}&content_type=${selectedContentType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRecommendedTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
    }
  };

  const handleSkip = async () => {
    try {
      setSubmitting(true);
      await axios.post(`${API}/api/onboarding/skip`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshUser();
      onComplete?.();
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to skip onboarding:', error);
      toast.error('Failed to skip onboarding');
    } finally {
      setSubmitting(false);
    }
  };

  const handleComplete = async () => {
    try {
      setSubmitting(true);
      const response = await axios.post(`${API}/api/onboarding/complete`, {
        goal: selectedGoal,
        content_type: selectedContentType,
        selected_template_id: selectedTemplate?.id || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await refreshUser();
      toast.success('Welcome to Delphi AI!');
      onComplete?.();
      
      // If a template was selected, store it for the studio
      if (selectedTemplate) {
        sessionStorage.setItem('onboarding_template', JSON.stringify(selectedTemplate));
      }
      
      navigate(response.data.redirect_path || '/dashboard');
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      toast.error('Failed to complete onboarding');
    } finally {
      setSubmitting(false);
    }
  };

  const nextStep = () => {
    if (step < 3) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 0) setStep(step - 1);
  };

  const canProceed = () => {
    switch (step) {
      case 0: return true; // Welcome - always can proceed
      case 1: return selectedGoal !== null;
      case 2: return selectedContentType !== null;
      case 3: return true; // Templates - optional selection
      default: return false;
    }
  };

  // Step 0: Welcome Screen
  const WelcomeStep = () => (
    <div className="text-center max-w-xl mx-auto">
      <div className="w-20 h-20 bg-primary/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
        <Sparkles className="w-10 h-10 text-primary" />
      </div>
      
      <h1 className="text-4xl font-bold mb-4">
        Welcome to <span className="text-primary">Delphi AI</span>
      </h1>
      
      <p className="text-lg text-muted-foreground mb-8">
        Your AI-powered content creation studio. Let's set up your experience 
        to help you create amazing content faster.
      </p>
      
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="p-4 bg-card border border-border rounded-xl">
          <Video className="w-6 h-6 text-primary mx-auto mb-2" />
          <p className="text-sm font-medium">Videos</p>
        </div>
        <div className="p-4 bg-card border border-border rounded-xl">
          <ImageIcon className="w-6 h-6 text-blue-400 mx-auto mb-2" />
          <p className="text-sm font-medium">Images</p>
        </div>
        <div className="p-4 bg-card border border-border rounded-xl">
          <Music className="w-6 h-6 text-purple-400 mx-auto mb-2" />
          <p className="text-sm font-medium">Music</p>
        </div>
      </div>
      
      <p className="text-sm text-muted-foreground">
        This will only take 30 seconds
      </p>
    </div>
  );

  // Step 1: Goal Selection
  const GoalStep = () => (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2">What's your main goal?</h2>
        <p className="text-muted-foreground">
          This helps us recommend the right templates for you
        </p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        {goals.map((goal) => {
          const Icon = iconMap[goal.icon] || Sparkles;
          const isSelected = selectedGoal === goal.id;
          
          return (
            <button
              key={goal.id}
              onClick={() => setSelectedGoal(goal.id)}
              className={`p-5 rounded-xl border-2 text-left transition-all ${
                isSelected 
                  ? 'border-primary bg-primary/10' 
                  : 'border-border bg-card hover:border-primary/50'
              }`}
              data-testid={`goal-${goal.id}`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-2.5 rounded-lg ${isSelected ? 'bg-primary/20' : 'bg-muted'}`}>
                  <Icon className={`w-5 h-5 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">{goal.name}</h3>
                  <p className="text-sm text-muted-foreground">{goal.description}</p>
                </div>
                {isSelected && (
                  <Check className="w-5 h-5 text-primary" />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  // Step 2: Content Type Selection
  const ContentTypeStep = () => (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2">What type of content?</h2>
        <p className="text-muted-foreground">
          Choose your primary content format
        </p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        {contentTypes.map((type) => {
          const Icon = iconMap[type.icon] || Sparkles;
          const isSelected = selectedContentType === type.id;
          
          const colorClasses = {
            video: 'text-primary bg-primary/20',
            image: 'text-blue-400 bg-blue-500/20',
            music: 'text-purple-400 bg-purple-500/20',
            package: 'text-emerald-400 bg-emerald-500/20'
          };
          
          return (
            <button
              key={type.id}
              onClick={() => setSelectedContentType(type.id)}
              className={`p-5 rounded-xl border-2 text-left transition-all ${
                isSelected 
                  ? 'border-primary bg-primary/10' 
                  : 'border-border bg-card hover:border-primary/50'
              }`}
              data-testid={`content-type-${type.id}`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-2.5 rounded-lg ${colorClasses[type.id] || 'bg-muted'}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">{type.name}</h3>
                  <p className="text-sm text-muted-foreground">{type.description}</p>
                </div>
                {isSelected && (
                  <Check className="w-5 h-5 text-primary" />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  // Step 3: Template Recommendations
  const TemplatesStep = () => (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2">Recommended for you</h2>
        <p className="text-muted-foreground">
          Start with a template or jump straight into the studio
        </p>
      </div>
      
      {recommendedTemplates.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          {recommendedTemplates.map((template) => {
            const isSelected = selectedTemplate?.id === template.id;
            
            return (
              <button
                key={template.id}
                onClick={() => setSelectedTemplate(isSelected ? null : template)}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  isSelected 
                    ? 'border-primary bg-primary/10' 
                    : 'border-border bg-card hover:border-primary/50'
                }`}
                data-testid={`template-${template.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  {template.is_featured && (
                    <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  )}
                  {isSelected && (
                    <Check className="w-4 h-4 text-primary ml-auto" />
                  )}
                </div>
                <h3 className="font-semibold text-sm mb-1 line-clamp-1">{template.name}</h3>
                <p className="text-xs text-muted-foreground line-clamp-2">{template.description}</p>
                <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  ~{Math.floor(template.estimated_time / 60) || 1} min
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <Zap className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No templates found. You can create from scratch in the studio!</p>
        </div>
      )}
      
      <div className="text-center">
        <p className="text-sm text-muted-foreground mb-2">
          {selectedTemplate 
            ? `Selected: "${selectedTemplate.name}"` 
            : 'Or skip templates and start fresh'}
        </p>
      </div>
    </div>
  );

  // Render current step
  const renderStep = () => {
    switch (step) {
      case 0: return <WelcomeStep />;
      case 1: return <GoalStep />;
      case 2: return <ContentTypeStep />;
      case 3: return <TemplatesStep />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="p-4 flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          {step > 0 && `Step ${step} of 3`}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSkip}
          disabled={submitting}
          className="text-muted-foreground hover:text-foreground"
          data-testid="skip-onboarding-btn"
        >
          <X className="w-4 h-4 mr-1" />
          Skip
        </Button>
      </div>
      
      {/* Step Indicator */}
      <StepIndicator currentStep={step} totalSteps={4} />
      
      {/* Step Content */}
      <div className="flex-1 flex items-center justify-center px-6 pb-24">
        {renderStep()}
      </div>
      
      {/* Footer Navigation */}
      <div className="fixed bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-background to-transparent">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          {step > 0 ? (
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={submitting}
              data-testid="onboarding-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          ) : (
            <div />
          )}
          
          {step < 3 ? (
            <Button
              onClick={nextStep}
              disabled={!canProceed() || submitting}
              className="gap-2"
              data-testid="onboarding-next-btn"
            >
              {step === 0 ? "Let's Go" : "Continue"}
              <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={handleComplete}
              disabled={submitting}
              className="gap-2"
              data-testid="onboarding-complete-btn"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Setting up...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4" />
                  {selectedTemplate ? 'Start with Template' : 'Go to Studio'}
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingFlow;
